// Authoritative simulation. Runs on the server (one instance per room) and in the browser for offline practice.
// Pure: no DOM, no Node APIs, no wall clock. Randomness comes from a seeded PRNG so tests are reproducible.
import {
  GameMap, Vec, Body, Stages, StageMasks, moveBody, collide, fieldFor, fieldAt, flowDir, stageMasks, buildOctileField, octileDir, buildField,
} from './map';
import type { MetroSpawn } from './metro';
import { cleanName } from './names';
import {
  ChaloData, ChaloGeo, ChaloSnap, Gate, GATE_CODE, LEVEL_SITE, chaloGeo, chaloMask, teamRow,
  CalmKind, LineKind, LINE_KINDS, baatDelta, dharnaCalm, playersNeeded, releasePoint,
} from './chalo';

export const TICK_RATE = 20;
export const DT = 1 / TICK_RATE;
/** Ticks a human's late inputs may catch up on (half a second): a burst after a network stall moves them as predicted. */
export const INPUT_BANK = TICK_RATE / 2;
export const NPC_SPEED = 0.55; // fraction of the player's speed
export { MAX_NAME, cleanName } from './names';

// Body scale. Maps with metres per tile (converted from OSM) use real units, matching the "scale" block of
// data/maps/jantar-mantar.spec.json; hand-made maps without mpt keep the prototype's px constants.
export const PLAYER_MPS = 16.0; // a toy-scale city: big bodies moving fast over real streets (docs/plans/2026-10-17-mob-streets.md; was 4, 5, 14)
export const PLAYER_R_M = 0.8;
export const NPC_R_M = 0.7;
const LEGACY = { speed: 150, player: 12, npc: 11 }; // px/s and px

type Scaled = Pick<GameMap, 'tile' | 'mpt'>;
/** Player walking speed in px/s. NPCs walk at NPC_SPEED of this. */
export const speedFor = (m: Scaled): number => (m.mpt ? (PLAYER_MPS * m.tile) / m.mpt : LEGACY.speed);
/** Body radius in px. */
export const radiusFor = (m: Scaled, kind: 'player' | 'npc'): number =>
  m.mpt ? ((kind === 'npc' ? NPC_R_M : PLAYER_R_M) * m.tile) / m.mpt : LEGACY[kind];

export type Team = 'P' | 'C';
/** lobby: waiting for players, countdown: about to start, playing: the round, roundOver: result screen, then back to lobby */
export type Phase = 'lobby' | 'countdown' | 'playing' | 'roundOver';

/** s: sequence, (x, y): wanted direction (length <= 1), r: 1 while jog is held */
export interface Input { s: number; x: number; y: number; r?: 0 | 1 }

// ------------------------------------------------------------------ moving a human (shared with client prediction)

export interface Stamina { st: number; jog: boolean; rest: number }
export const freshStamina = (): Stamina => ({ st: 1, jog: false, rest: 0 });

/**
 * One tick of stamina. Jogging drains it; after regenDelayS without jogging it refills; a jog can only start at
 * minToStart or more. Returns true when this tick is jogged.
 */
export function stepStamina(b: Stamina, want: boolean, J: Stages['jog'], dt: number): boolean {
  if (want && b.st > 0 && (b.jog || b.st >= J.minToStart)) {
    b.jog = true; b.rest = 0; b.st = Math.max(0, b.st - J.drainPerS * dt);
    if (b.st === 0) b.jog = false;
    return true;
  }
  b.jog = false; b.rest += dt;
  if (b.rest >= J.regenDelayS - 1e-9) b.st = Math.min(1, b.st + J.regenPerS * dt);
  return false;
}

/** What moving a human depends on besides its input: speeds in px/s, the jog rules (if any) and the play area. */
export interface MoveRules { speed: number; jogSpeed: number; jog?: Stages['jog']; mask?: Uint8Array }

/** One tick of a human's movement: stamina, then the body. The server and the predicting client both call this. */
export function moveHuman(m: GameMap, b: Body & Stamina, x: number, y: number, r: number, R: MoveRules, dt: number): number {
  const len = Math.hypot(x, y);
  if (len > 1) { x /= len; y /= len; }
  const jogging = R.jog ? stepStamina(b, r === 1 && len > 0, R.jog, dt) : false;
  return moveBody(m, b, x, y, jogging ? R.jogSpeed : R.speed, dt, R.mask);
}

// ------------------------------------------------------------------ the sim

/** friend: one of the player's toli (story mode), who follows them about and, on a staged map, counts as kafila */
export type Role = 'kafila' | 'ambient' | 'toli' | 'police' | 'friend';

/**
 * Chalo: a toli is one entity of up to toliSize people walking gate to gate (see thinkToli). Once it reaches the site
 * it stops routing and starts holding, and the second row of fields takes over: its hausla (nerve), and what the last
 * survey found about where it stands in the crowd (docs/plans/2026-10-18-crowd-as-structure.md).
 */
export interface Toli {
  n: number; gate: number; pass: number; wait: number; loss: number; spot: number;
  /** 1 steady, 0 gone. Frays on the crowd's edge, near a cordon and when crushed; steadies when a player stands by. */
  nerve: number;
  /** from the last survey: neighbours within nearM, whether this toli is on the crowd's rim, whether it is crushed */
  near: number; edge: boolean; crush: boolean;
  /** nerve below driftBelow: edging out of the crowd, and moving slower than a toli with somewhere to be */
  drift: boolean;
}

export interface Ent extends Body, Stamina {
  id: number;
  kind: 'human' | 'npc';
  team: Team | 'N';
  name: string;
  face: number;
  ack: number; // last input sequence number consumed (humans)
  inq: Input[];
  bank: number; // humans: ticks owed to inputs that have not arrived yet (see stepBodies), at most INPUT_BANK
  last: { x: number; y: number; r: number };
  /** npcs: ambient wander between pockets; kafila are the stage's crowd */
  role: Role | null;
  target: number; // ambient: index into the target list; kafila: index into the stage's points
  arrived: boolean; // kafila: reached the zone and its pad
  lane: number; // kafila: -1..1, which side of the route they keep to, so a march fills the road instead of a queue
  idleT: number;
  stuckT: number;
  /** humans: the face they wear (src/shared/avatars.ts), -1 for none */
  av: number;
  sentNames: boolean; // true once this human has been sent the name table (so a new joiner sees names at once)
  toli?: Toli;
  /** police (Chalo): the checkpoint index and place in its squad */
  cp?: number; slot?: number;
  /** story mode: unbounded by the stage area until first inside it (an arrival from the map's edge or the zone) */
  free?: boolean;
  /** friends: their place in the toli (0, 1, ...), which picks their look and where they walk behind the player */
  k?: number;
  /** Chalo, a player: seconds left in the police bus (0: free), and the checkpoint they were taken at */
  held?: number; heldCp?: number;
  /** Chalo, a player: the gate they sit in at (Dharna), -1 standing */
  sit?: number;
  /** the waiting lobby: the metro exit (index into lobby.spawns, else map.spawnsP) a human was planted at, -1 when placed otherwise */
  gate?: number;
}

/** Baat in progress at a gate: whose talk it is, the exchange and mood, the time left to pick, the hint and the options */
interface Talk { who: number; ex: number; mood: number; left: number; hint: [CalmKind, number]; opts: [LineKind, number][]; jog: boolean; last: number }
/** Dharna at a gate: seconds held at or above the pressure, seconds below the break line, seconds since it began */
interface SitIn { held: number; low: number; t: number; calm: number; sitters: number }

/**
 * Story mode's arrival (docs/plans/2026-10-17-journey.md, section 5): where the player appears for the first round, the
 * stage they join and how long it has run, and who comes with them. The kafila that gathered meanwhile are already there.
 */
export interface SimArrival { playerAt: Vec; stage: 0 | 1; stageT: number; companions: number; skipGather: boolean }

export interface SimOptions {
  roundTime: number; // seconds
  countdown: number;
  overTime: number;
  minPlayers: number; // humans needed to leave the lobby
  /** npcs on a map without stages; with stages, the ambient count (default: the stages file's) */
  npcs: number;
  seed: number;
  /** stage areas and pacing (data/maps/<map>.stages.json); null keeps the free-roaming round */
  stages: Stages | null;
  /** index into stages.starts for every round; default: the default starts in turn */
  start: number | null;
  /** Chalo mode (rings and rules); null keeps the stages round. Defaults the round times from the rules. */
  chalo: ChaloData | null;
  /** accept debug actions (open the nearest gate); offline practice and tests */
  debug: boolean;
  /**
   * The tending loop (docs/plans/2026-10-18-crowd-as-structure.md), off by default: tolis holding the site keep a
   * hausla that frays and is steadied by a protester standing with them. It is a prototype and it changes Chalo's
   * arithmetic -- with nobody tending, a gathering crowd bleeds faster than it builds -- so Chalo's own round keeps
   * it off and plays exactly as before.
   */
  hold: boolean;
  /** story mode: the journey's result, applied to the first round; null for the ordinary rounds */
  arrival: SimArrival | null;
  /** story mode: friends who follow the first player from the start (the doorstep's toli) */
  friends: number;
  /** a room of friends (docs/plans/2026-10-18-date-raho.md, section 7): its protesters arrive together, as one toli, at one entry a round */
  party: boolean;
  /**
   * The waiting lobby (docs/plans/2026-10-18-waiting-lobby.md): the round waits for `size` humans, and until it starts
   * each human is planted within spreadM of a metro exit: a random station of `spawns`, then one of its exits (the city
   * lobby, docs/plans/2026-10-18-city-lobby.md), or of map.spawnsP without them. null keeps the ordinary spawns.
   */
  lobby: { size: number; spreadM: number; spawns?: MetroSpawn[] } | null;
  /**
   * Roam the city (docs/plans/2026-10-19-fixes-round-1.md, section C): `stages` gives only the jog rules and the
   * area of interest; no stage arc, no play area. The client pairs it with a round that never runs out.
   */
  roam: boolean;
}
export const DEFAULTS: SimOptions = { roundTime: 480, countdown: 3, overTime: 8, minPlayers: 1, npcs: 60, seed: 1947, stages: null, start: null, chalo: null, debug: false, hold: false, arrival: null, friends: 0, party: false, lobby: null, roam: false };
export const SQUAD = 3; // police per checkpoint (Chalo)
export const MAX_TOLIS = 160; // keeps the sim under the plan's 210 entities

export interface Snapshot {
  t: 's';
  tk: number;
  ph: Phase;
  /** seconds left in the current phase (round time while playing) */
  pt: number;
  round: number;
  n: { P: number; C: number };
  /** st, jg, rs: stamina, jogging (0/1), seconds since the last jog; so prediction can replay jogs exactly */
  me: { id: number; team: Team; x: number; y: number; vx: number; vy: number; ack: number; st: number; jg: number; rs: number; dt?: number; si?: number } | null;
  /** rows: [id, type (0 protester, 1 police, 2 npc, 3 toli, 4 friend), x, y, face*100, toli people, friend index, or for an npc 1 kafila / 0 ambient] */
  e: number[][];
  /** display names of the humans; sent once a second rather than every tick (names rarely change) */
  /** the humans' names once a second: id, name, face (-1 for none) */
  nm?: [number, string, number][];
  /** stage index into STAGES while playing a staged round, else -1; start index; seconds into the stage */
  sg?: number; sv?: number; sgt?: number;
  /** kafila counts: [all, in Gather, in March, at the zone and its pad] */
  k?: number[];
  /** Chalo: gates, rings and counts */
  ch?: ChaloSnap;
  /** 1 while the player is not yet bounded by the stage area (story mode's arrival) */
  fr?: number;
}

export function mulberry32(a: number): () => number {
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}


/** The start used for a round (0-based) when none is forced: the default starts in turn. */
export function startFor(st: Stages, round: number): number {
  const d = st.starts.map((s, i) => (s.default ? i : -1)).filter((i) => i >= 0);
  return d.length ? d[round % d.length] : 0;
}

/** Tiles spread evenly through a mask (deterministic, so every sim on the map shares the cached fields). */
function spreadPoints(m: GameMap, k: Uint8Array, n: number): Vec[] {
  const tiles: number[] = [];
  for (let i = 0; i < k.length; i++) if (k[i]) tiles.push(i);
  const out: Vec[] = [];
  for (let j = 0; j < n && tiles.length; j++) {
    const i = tiles[Math.floor(((j + 0.5) * tiles.length) / n)];
    out.push({ x: ((i % m.w) + 0.5) * m.tile, y: (((i / m.w) | 0) + 0.5) * m.tile });
  }
  return out;
}

/** Everything the kafila need for one start: the masks, points to mill around, and the march field. */
export interface StagePlan { masks: StageMasks; gatherPts: Vec[]; standPts: Vec[]; march: Float32Array; key: string }
export function stagePlan(m: GameMap, st: Stages, start: number): StagePlan {
  const c = m as GameMap & { _plans?: Map<number, StagePlan> };
  const hit = c._plans?.get(start);
  if (hit) return hit;
  const masks = stageMasks(m, st, start);
  const standDest = masks.pad.map((v, i) => v & masks.stand[i]);
  const zone: number[] = [];
  for (let i = 0; i < masks.toZone.length; i++) if (masks.toZone[i] === 0 && masks.stand[i]) zone.push(i);
  const p: StagePlan = {
    masks, gatherPts: spreadPoints(m, masks.gather, 8), standPts: spreadPoints(m, standDest, 12),
    march: buildOctileField(m, zone, masks.stand), key: 'stage:' + start,
  };
  (c._plans ??= new Map()).set(start, p);
  return p;
}

export function createSim(map: GameMap, opts: Partial<SimOptions> = {}) {
  const CR = opts.chalo?.rules;
  const o: SimOptions = {
    ...DEFAULTS, ...(CR ? { countdown: CR.round.introS, roundTime: CR.round.gatherS, minPlayers: CR.players.min, npcs: 0 } : {}), ...opts,
  };
  if (o.lobby) o.minPlayers = o.lobby.size;
  const rnd = mulberry32(o.seed);
  const rand = (a: number, b: number) => a + rnd() * (b - a);
  const JOG = o.stages, ST = o.roam ? null : o.stages; // roam keeps the stages' jog rules, not their arc
  const CH = o.chalo, G: ChaloGeo | null = CH ? chaloGeo(map, CH) : null;

  const S = {
    tick: 0, phase: 'lobby' as Phase, phaseT: 0, roundT: o.roundTime, round: 0, nid: 1, ents: [] as Ent[],
    /** index into STAGES while a staged round is playing, else -1 */
    stage: -1, stageT: 0, start: ST ? (o.start ?? startFor(ST, 0)) : -1, arrive: 0,
    /** Chalo: the gates (one per checkpoint), seconds into the gather, fallen rings as bits, people counts, result */
    gates: [] as Gate[], chT: 0, fallen: 0, goal: 0, arrived: 0, left: 0, won: -1,
    /** Chalo: per gate, Baat and Dharna under way (null: none); players detained this round */
    talks: [] as (Talk | null)[], sits: [] as (SitIn | null)[], detained: 0,
  };
  const byId = new Map<number, Ent>();

  // npc targets: every pocket, then the plaza
  const PLAZA = map.pockets.length;
  const targetKey = (i: number) => (i === PLAZA ? 'plaza' : 'pocket:' + i);
  const fieldOf = (i: number) => fieldFor(map, targetKey(i), () => [i === PLAZA ? map.plaza : map.pockets[i]]);
  const plazaArrive = Math.floor((map.plaza.r / map.tile) * 0.6);
  const SPEED = speedFor(map), PLAYER_R = radiusFor(map, 'player'), NPC_R = radiusFor(map, 'npc');
  const METRE = map.mpt ? map.tile / map.mpt : 1;
  const RULES: MoveRules = { speed: SPEED, jogSpeed: CR && ST ? CR.players.jogMps * METRE : JOG ? JOG.jog.speedMps * METRE : SPEED, jog: JOG?.jog };
  const TOLI_R = 1.2 * METRE, TOLI_SPEED = CR ? CR.crowd.speedMps * METRE : SPEED;
  const AOI = JOG ? JOG.aoi.radiusM * METRE : Infinity;
  /** the personal space separation keeps between bodies on top of their radii, px (none without a stages file) */
  const GAP = JOG ? (JOG.separation.personalSpaceM ?? 0) * METRE : 0;
  // jitter around a spawn point, in tiles (110 px and 40 px on the 40 px prototype tiles)
  const JITTER = { npc: 2.75 * map.tile, human: map.tile };

  function mk(kind: 'human' | 'npc', team: Team | 'N', name: string, role: Role | null = null): Ent {
    const e: Ent = {
      id: S.nid++, kind, team, name, x: 0, y: 0, vx: 0, vy: 0, r: kind === 'npc' ? NPC_R : PLAYER_R, face: Math.PI / 2,
      ack: 0, inq: [], bank: 0, last: { x: 0, y: 0, r: 0 }, role, target: 0, arrived: false, lane: 0, idleT: 0, stuckT: 0, av: -1, sentNames: false,
      ...freshStamina(),
    };
    S.ents.push(e); byId.set(e.id, e);
    return e;
  }

  const plan = () => stagePlan(map, ST!, S.start);
  const tileOf = (e: Vec) => Math.floor(e.y / map.tile) * map.w + Math.floor(e.x / map.tile);
  /** The play area that holds a body now, or undefined when it is free (police, ambient npcs, outside a staged round). */
  function maskOf(e: Ent): Uint8Array | undefined {
    if (G) return S.phase === 'playing' && (e.role === 'toli' || (e.kind === 'human' && e.team === 'P')) ? chaloMask(map, G, gapBits()) : undefined;
    if (!ST || S.stage < 0 || e.free) return undefined;
    if (e.role === 'kafila' || e.role === 'friend' || (e.kind === 'human' && e.team === 'P')) return [plan().masks.gather, plan().masks.march, plan().masks.stand][S.stage];
    return undefined;
  }

  // jitter must not hop a fence: keep the jittered point only if it is reachable from the plaza (and in the play area)
  const reach = () => fieldFor(map, 'plaza', () => [map.plaza]);
  function placeAt(e: Ent, p: Vec, jitter: number) {
    const k = maskOf(e);
    let x = p.x, y = p.y;
    for (let i = 0; i < 4; i++) {
      const jx = p.x + rand(-jitter, jitter), jy = p.y + rand(-jitter, jitter);
      if (fieldAt(map, reach(), jx, jy) >= 0 && (!k || k[tileOf({ x: jx, y: jy })])) { x = jx; y = jy; break; }
    }
    e.x = x; e.y = y; e.vx = e.vy = 0;
    collide(map, e, e.r, k);
  }

  const gate = () => map.spawnsP[ST!.starts[S.start].spawnP];

  /** the first round of a story episode places the player and their friends where the journey left them */
  const arriving = () => !!o.arrival && S.round <= 1;
  function respawn(e: Ent) {
    if (e.role === 'friend') {
      const lead = leader();
      placeAt(e, arriving() ? o.arrival!.playerAt : lead ?? map.spawnsP[0], map.tile * 0.8); e.free = arriving(); e.idleT = 0; return;
    }
    if (e.role === 'police') { Object.assign(e, post(e)); e.vx = e.vy = 0; return; }
    if (e.role === 'kafila') {
      placeAt(e, gate(), map.tile); e.target = Math.floor(rnd() * 12); e.arrived = false; e.idleT = 0; e.lane = rand(-1, 1); return;
    }
    if (e.kind === 'npc') {
      const p = map.pockets[Math.floor(rnd() * map.pockets.length)];
      placeAt(e, p, JITTER.npc); e.target = pickTarget(); e.idleT = rand(0, 3); return;
    }
    e.held = 0; e.sit = -1;
    const list = e.team === 'P' ? (G ? [G.entries[(o.party ? S.round : e.id) % G.entries.length]] : ST ? [gate()] : o.party ? [map.spawnsP[S.round % map.spawnsP.length]] : map.spawnsP) : map.spawnsC;
    e.gate = -1;
    if (o.lobby && S.phase !== 'playing' && map.spawnsP.length) { // waiting for the room: planted near a metro exit, either side
      e.gate = lobbySpawn();
      placeAt(e, o.lobby.spawns?.[e.gate] ?? map.spawnsP[e.gate], o.lobby.spreadM * METRE); e.free = false;
    } else if (e.team === 'P' && arriving()) { placeAt(e, o.arrival!.playerAt, 0); e.free = true; }
    else { placeAt(e, list[Math.floor(rnd() * list.length)], JITTER.human); e.free = false; }
    e.inq.length = 0; e.bank = 0; e.last = { x: 0, y: 0, r: 0 }; Object.assign(e, freshStamina());
  }

  /** a station uniformly (Rajiv Chowk's ten exits don't make it ten times likelier), then one of its exits */
  function lobbySpawn(): number {
    const L = o.lobby!.spawns;
    if (!L?.length) return Math.floor(rnd() * map.spawnsP.length);
    const names = [...new Set(L.map((x) => x.station))], st = names[Math.floor(rnd() * names.length)];
    const at = L.map((x, i) => (x.station === st ? i : -1)).filter((i) => i >= 0);
    return at[Math.floor(rnd() * at.length)];
  }

  const pickTarget = () => (rnd() < 0.35 ? PLAZA : Math.floor(rnd() * map.pockets.length));

  function populateNpcs() {
    const n = ST && !CH && opts.npcs === undefined ? ST.ambient.count : o.npcs;
    for (let i = 0; i < n; i++) respawn(mk('npc', 'N', '', 'ambient'));
  }
  populateNpcs();
  // Chalo: a police squad per checkpoint, standing in its gap
  if (G) G.cps.forEach((_, k) => { for (let j = 0; j < SQUAD; j++) { const p = mk('npc', 'N', '', 'police'); p.cp = k; p.slot = j; Object.assign(p, post(p)); } });

  const humans = () => S.ents.filter((e) => e.kind === 'human');
  const kafila = () => S.ents.filter((e) => e.role === 'kafila');
  const friends = () => S.ents.filter((e) => e.role === 'friend');
  /** the player the friends follow: the first protester */
  const leader = () => S.ents.find((e) => e.kind === 'human' && e.team === 'P');
  function addFriends(n: number) {
    const have = friends().length;
    for (let k = 0; k < n; k++) { const f = mk('npc', 'N', '', 'friend'); f.k = have + k; respawn(f); }
  }
  function removeKafila() {
    for (const e of kafila()) byId.delete(e.id);
    S.ents = S.ents.filter((e) => e.role !== 'kafila');
  }

  // ------------------------------------------------------------------ public api
  function addPlayer(name: unknown, team: unknown, av = -1): Ent {
    const t: Team = team === 'C' && !CH && !o.roam ? 'C' : 'P'; // Chalo is co-op: police are the sim's; roam has none
    const e = mk('human', t, cleanName(name));
    e.av = av;
    respawn(e);
    const want = (o.arrival ? o.arrival.companions : o.friends) - friends().length;
    if (e.team === 'P' && want > 0) addFriends(want);
    return e;
  }
  /**
   * Put a human down at p: the shared metro's arrivals (docs/plans/2026-10-18-shared-metro.md), stepping out where their
   * train let them off. `free`: unbounded by the stage area until first inside it, like story mode's arrival.
   */
  function place(id: number, p: Vec, free: boolean) {
    const e = byId.get(id);
    if (!e || e.kind !== 'human') return;
    placeAt(e, p, 0); e.free = free;
  }
  function removePlayer(id: number) {
    const i = S.ents.findIndex((e) => e.id === id);
    if (i >= 0) S.ents.splice(i, 1);
    byId.delete(id);
  }
  function setTeam(id: number, team: unknown) {
    const e = byId.get(id);
    if (!e || e.kind !== 'human' || CH || o.roam) return; // Chalo is co-op and roam is on foot: no police players
    const t: Team = team === 'C' ? 'C' : 'P';
    if (e.team !== t) { e.team = t; respawn(e); }
  }
  /** A human's name and face change (character select locks them in): every human gets the name table in their next snapshot. */
  function rename(id: number, name: string, av: number) {
    const e = byId.get(id);
    if (!e || e.kind !== 'human') return;
    e.name = cleanName(name); e.av = av;
    for (const h of humans()) h.sentNames = false;
  }
  /** While `f` says so the lobby does not start its countdown: a room holds it while anyone is still picking a face. */
  let lobbyHeld: (() => boolean) | null = null;
  function holdLobby(f: (() => boolean) | null) { lobbyHeld = f; }
  function input(id: number, inp: unknown) {
    const e = byId.get(id);
    if (!e || e.kind !== 'human' || typeof inp !== 'object' || inp === null) return;
    const { s, x, y, r } = inp as Record<string, unknown>;
    if (!Number.isInteger(s) || typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) return;
    if (r !== undefined && r !== 0 && r !== 1) return;
    if (e.inq.length >= INPUT_BANK * 2) e.inq.shift();
    e.inq.push({ s: s as number, x, y, r: r === 1 ? 1 : 0 });
  }

  // ------------------------------------------------------------------ tick
  function startRound() {
    S.round++; S.roundT = o.roundTime; S.phase = 'playing';
    if (G) resetChalo();
    else if (ST) {
      S.start = o.start ?? startFor(ST, S.round - 1); S.stage = 0; S.stageT = 0; S.arrive = 0; removeKafila();
      if (arriving()) { // the stage has been running without the player: its crowd has already come (and set off)
        const A = o.arrival!;
        S.stage = A.stage; S.stageT = A.stageT;
        S.arrive = (A.stage === 1 ? ST.gather.maxS + A.stageT : A.stageT) * ST.gather.arrivalPerS;
      }
    }
    for (const e of S.ents) respawn(e);
  }

  function updatePhase() {
    const n = humans().length;
    switch (S.phase) {
      case 'lobby':
        if (n >= o.minPlayers && !lobbyHeld?.()) { S.phase = 'countdown'; S.phaseT = o.countdown; }
        break;
      case 'countdown':
        if (n < o.minPlayers) { S.phase = 'lobby'; break; }
        S.phaseT -= DT;
        if (S.phaseT <= 0) startRound();
        break;
      case 'playing':
        S.roundT -= DT;
        if (S.roundT <= 0) { S.phase = 'roundOver'; S.phaseT = o.overTime; S.stage = -1; if (G) S.won = siteCount() >= S.goal ? 1 : 0; }
        break;
      case 'roundOver':
        S.phaseT -= DT;
        if (S.phaseT <= 0) {
          S.phase = 'lobby';
          if (G) { removeTolis(); S.won = -1; }
          else if (ST) { removeKafila(); S.start = o.start ?? startFor(ST, S.round); }
          for (const e of humans()) respawn(e);
        }
        break;
    }
  }

  /** Stage clock, crowd arrivals, and moving on to the next stage (see the stages file's advance rules). */
  function updateStage() {
    if (!ST || G || S.phase !== 'playing' || S.stage < 0) return;
    S.stageT += DT;
    const target = [ST.gather.crowd, ST.march.crowd, ST.stand.crowd][S.stage];
    // friends count toward the crowd, unless they skipped the gathering with the player (dropped at the zone)
    const own = o.arrival?.skipGather && S.round <= 1 ? 0 : friends().length;
    let n = kafila().length;
    S.arrive += ST.gather.arrivalPerS * DT;
    for (; S.arrive >= 1; S.arrive--) if (n + own < target) { respawn(mk('npc', 'N', '', 'kafila')); n++; }
    if (S.stage === 0) {
      if ((n + own >= ST.gather.crowd && S.stageT >= ST.gather.minS) || S.stageT >= ST.gather.maxS) nextStage();
    } else if (S.stage === 1) {
      const { pad } = plan().masks;
      const onZone = humans().some((e) => e.team === 'P' && !e.free && map.rows[Math.floor(e.y / map.tile)]?.[Math.floor(e.x / map.tile)] === 'P');
      const atZone = kafila().filter((e) => pad[tileOf(e)]).length;
      if (onZone || (n > 0 && atZone * 2 >= n) || S.stageT >= ST.march.maxS) nextStage();
    }
  }
  function nextStage() { S.stage++; S.stageT = 0; }
  /**
   * Story mode: minutes the player spends off the clock (the poster bazaar, docs/plans/2026-10-18-poster-bazaar.md), as
   * seconds of Gather that run on without them: its clock and its arrivals. The next tick moves on if that ends Gather.
   * The March is measured in metres walked, not seconds, so it is not moved.
   */
  function spend(seconds: number) {
    if (!ST || G || S.phase !== 'playing' || S.stage !== 0 || seconds <= 0) return;
    S.stageT += seconds; S.arrive += ST.gather.arrivalPerS * seconds;
  }

  // ------------------------------------------------------------------ Chalo (docs/plans/2026-10-17-chalo.md)
  /** bit k set while checkpoint k's gap is walkable (open, or police still walking back) */
  let bitsCache = -1;
  function gapBits(): number {
    if (bitsCache < 0) { bitsCache = 0; S.gates.forEach((g, k) => { if (g.state === 'open' || g.state === 'back') bitsCache |= 1 << k; }); }
    return bitsCache;
  }
  const walkable = (k: number) => ((gapBits() >> k) & 1) === 1;
  const ringFallen = (r: number) => ((S.fallen >> r) & 1) === 1;
  const tolis = () => S.ents.filter((e) => e.role === 'toli');
  const onSite = (e: Ent) => { const i = tileOf(e); return G!.level[i] === LEVEL_SITE && !G!.gapOf[i]; };
  const siteCount = () => tolis().reduce((n, e) => n + (onSite(e) ? e.toli!.n : 0), 0);

  function resetChalo() {
    removeTolis();
    S.gates = G!.cps.map(() => ({ state: 'closed', until: 0, opened: false, credit: 0, byTalk: false, cool: 0, slow: false, sitIn: false }));
    S.talks = G!.cps.map(() => null); S.sits = G!.cps.map(() => null); S.detained = 0;
    bitsCache = -1; S.chT = 0; S.fallen = 0; S.arrive = 0; S.arrived = 0; S.left = 0; S.won = -1;
    S.goal = teamRow(CR!, humans().length).goal;
    for (const e of S.ents) if (e.role === 'police') Object.assign(e, post(e), { vx: 0, vy: 0 });
  }
  function removeTolis() {
    for (const e of tolis()) byId.delete(e.id);
    S.ents = S.ents.filter((e) => e.role !== 'toli');
  }

  /** Where a police officer stands: spread across the gap while it is closed (or being re-closed), else aside. */
  function post(e: Ent): Vec {
    const cp = G!.cps[e.cp!], st = S.gates[e.cp!]?.state ?? 'closed', j = e.slot!;
    if (st === 'closed' || st === 'back' || st === 'game') { // a game is played against the squad in the gap
      const t = cp.gaps[Math.floor(((j + 0.5) * cp.gaps.length) / SQUAD)];
      return { x: ((t % map.w) + 0.5) * map.tile, y: (((t / map.w) | 0) + 0.5) * map.tile };
    }
    return { x: cp.aside.x + (j - 1) * 1.6 * METRE, y: cp.aside.y + ((j % 2) - 0.5) * 1.2 * METRE };
  }

  /** The re-close factor now: the director's timeline (reinforcements) scales how long a gate stays open. */
  function recloseFactor(): number {
    let f = 1;
    for (const ev of CR!.director.timeline) if (ev.atS <= S.chT && ev.recloseFactor) f *= ev.recloseFactor;
    return f;
  }

  /** Open checkpoint k (a won game, or the debug key). A ring that has enough opened gates falls and stays open. */
  function openGate(k: number, byTalk = false) {
    const g = S.gates[k], cp = G!.cps[k];
    if (!g || g.state === 'open') return;
    const row = teamRow(CR!, humans().length), ring = G!.ringNames[cp.ring];
    g.state = 'open'; g.opened = true; g.byTalk ||= byTalk;
    g.until = ringFallen(cp.ring) ? Infinity : S.chT + (row.openS[ring] ?? 30) * recloseFactor();
    bitsCache = -1;
    const need = row.ringFalls[ring];
    if (need && !ringFallen(cp.ring) && S.gates.filter((h, i) => h.opened && G!.cps[i].ring === cp.ring).length >= need) {
      S.fallen |= 1 << cp.ring;
      S.gates.forEach((h, i) => { if (G!.cps[i].ring === cp.ring) { h.state = 'open'; h.until = Infinity; } });
    }
  }

  // ------------------------------------------------------------------ detention, Dharna and Baat (Chalo build step 2)
  /** the first ring still standing, counting from outside: the ring in front of anyone outside the kettle */
  function frontRing(): number {
    let r = 0;
    while (r < G!.ringNames.length - 1 && ringFallen(r)) r++;
    return r;
  }
  /** Take a player to the bus for detainedS. Never shown as force: they are simply at the bus, on a timer. */
  function detain(e: Ent, k: number) {
    if (e.held) return;
    for (const [j, t] of S.talks.entries()) if (t && t.who === e.id) endTalk(j, false);
    e.held = CR!.players.detainedS; e.heldCp = k; e.sit = -1;
    placeAt(e, { x: G!.bus.x + map.tile, y: G!.bus.y }, 0); e.vx = e.vy = 0; e.inq.length = 0; // beside the bus, not under its marker
    S.detained++;
  }
  function stepHeld() {
    for (const e of humans()) {
      if (!e.held) continue;
      e.held = Math.max(0, e.held - DT);
      if (e.held > 0) continue;
      const live = G!.entries.filter((n) => n.closesAtS === null || S.chT < n.closesAtS);
      placeAt(e, releasePoint(map, G!, G!.cps[e.heldCp ?? 0].at, frontRing(), live), 0);
      Object.assign(e, freshStamina());
    }
  }
  const DH = () => CR!.missions.dharna.play, BT = () => CR!.missions.baat.play;
  const near = (e: Ent, p: Vec, m: number) => Math.hypot(e.x - p.x, e.y - p.y) <= m * METRE;
  /** a body in front of checkpoint k, outside its ring (the side the crowd waits on) */
  const before = (e: Ent, k: number) => G!.level[tileOf(e)] === G!.cps[k].ring && !G!.gapOf[tileOf(e)];
  const gameAt = (k: number) => G!.cps[k].mission;

  /** E: sit in at an inner gate, talk at the inspector's gate, or stand up again. */
  function use(e: Ent) {
    if (e.held || S.phase !== 'playing') return;
    if ((e.sit ?? -1) >= 0) { e.sit = -1; return; }
    let best = -1, bd = Infinity;
    G!.cps.forEach((cp, k) => {
      const g = S.gates[k], m = gameAt(k), d = Math.hypot(cp.at.x - e.x, cp.at.y - e.y);
      const reach = m === 'dharna' ? DH().sitWithinM : m === 'baat' ? BT().talkWithinM : 0;
      if (!reach || d > reach * METRE || d >= bd || !before(e, k) || S.chT < g.cool) return;
      if (m === 'dharna' && (g.state === 'closed' || g.state === 'game' || (g.state === 'open' && g.sitIn))) { best = k; bd = d; }
      if (m === 'baat' && g.state === 'closed' && !S.talks[k]) { best = k; bd = d; }
    });
    if (best < 0) return;
    if (gameAt(best) === 'dharna') e.sit = best;
    else startTalk(best, e);
  }

  /** Dharna at each inner gate: calm against the squad's pressure; hold it for durationS and the gate opens. */
  function stepDharna() {
    const P = DH(), M = CR!.missions.dharna, need = playersNeeded(M.playersMin, humans().length);
    G!.cps.forEach((cp, k) => {
      if (gameAt(k) !== 'dharna') return;
      const g = S.gates[k];
      // a sitter who has walked off, or been taken, is no longer sitting
      const sitters = humans().filter((e) => e.sit === k && !e.held && near(e, cp.at, P.sitWithinM));
      for (const e of humans()) if (e.sit === k && !sitters.includes(e)) e.sit = -1;
      if (g.state === 'open' && g.sitIn) { // it stays open while the sit-in holds (up to holdS)
        if (!sitters.length) { g.until = S.chT; g.sitIn = false; }
        return;
      }
      const s = S.sits[k];
      if (sitters.length < need) { // not enough to begin, or the sit-in has thinned out: no harm, it just ends
        if (s) { S.sits[k] = null; if (g.state === 'game') g.state = 'closed'; }
        return;
      }
      if (g.state !== 'closed' && g.state !== 'game') return;
      const t = s ?? (S.sits[k] = { held: 0, low: 0, t: 0, calm: 0, sitters: 0 });
      g.state = 'game';
      const queued = tolis().filter((q) => before(q, k) && near(q, cp.at, P.toliWithinM)).length;
      const pressure = S.ents.filter((p) => p.role === 'police' && p.cp === k).length;
      t.calm = dharnaCalm(P, sitters.length, queued); t.sitters = sitters.length; t.t += DT;
      if (t.calm >= pressure) { t.held += DT; t.low = 0; }
      else if (t.calm < pressure * P.breakBelow) t.low += DT;
      else t.low = 0;
      if (t.held >= M.durationS) {
        S.sits[k] = null; g.state = 'closed';
        openGate(k);
        g.until = S.chT + teamRow(CR!, humans().length).holdS; g.sitIn = true;
      } else if (t.low >= P.breakAfterS) { // the sit-in breaks: the sitter nearest the line is detained
        S.sits[k] = null; g.state = 'closed';
        const front = sitters.reduce((a, b) => (Math.hypot(b.x - cp.at.x, b.y - cp.at.y) < Math.hypot(a.x - cp.at.x, a.y - cp.at.y) ? b : a));
        for (const e of sitters) e.sit = -1;
        detain(front, k);
      } else if (t.t >= M.limitS) { S.sits[k] = null; g.state = 'closed'; for (const e of sitters) e.sit = -1; }
    });
  }

  /** a fresh exchange: the inspector hints at one calm kind; the options are that kind, another calm one and heat, shuffled */
  function exchange(t: Talk) {
    const SC = CR!.missions.baat.script, calm: CalmKind[] = ['facts', 'rights', 'people'];
    const pick = <T,>(a: T[]) => a[Math.floor(rnd() * a.length)];
    const hint = pick(calm), other = pick(calm.filter((c) => c !== hint));
    t.hint = [hint, Math.floor(rnd() * SC.hints[hint].length)];
    const opts: [LineKind, number][] = ([hint, other, 'heat'] as LineKind[]).map((kd) => [kd, Math.floor(rnd() * SC.lines[kd].length)]);
    for (let i = opts.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [opts[i], opts[j]] = [opts[j], opts[i]]; }
    t.opts = opts; t.left = BT().pickS; t.jog = false;
  }
  function startTalk(k: number, e: Ent) {
    const t: Talk = { who: e.id, ex: 0, mood: BT().moodStart, left: 0, hint: ['facts', 0], opts: [], jog: false, last: 0 };
    exchange(t);
    S.talks[k] = t; S.gates[k].state = 'game';
  }
  function endTalk(k: number, won: boolean) {
    const g = S.gates[k];
    S.talks[k] = null; g.state = 'closed';
    if (won) { openGate(k, true); g.until = S.chT + BT().openS; g.slow = true; } // re-closed at half speed: see the police step
    else g.cool = S.chT + BT().retryS; // try again later; nobody is detained
  }
  /** the line picked (null: time ran out) is weighed against the hint; five exchanges, or mood at 0, end the talk */
  function answer(k: number, kind: LineKind | null) {
    const t = S.talks[k]!, P = BT();
    t.last = baatDelta(P, kind, t.hint[0], t.jog);
    t.mood = Math.max(0, Math.min(100, t.mood + t.last)); t.ex++;
    if (t.mood >= P.moodWin) endTalk(k, true);
    else if (t.mood <= 0 || t.ex >= P.exchanges) endTalk(k, false);
    else exchange(t);
  }
  function stepBaat() {
    const P = BT();
    S.talks.forEach((t, k) => {
      if (!t) return;
      const who = byId.get(t.who), cp = G!.cps[k];
      if (!who || who.held || !near(who, cp.at, P.talkWithinM * 1.5)) { endTalk(k, false); return; } // walked off
      if (humans().some((e) => e.jog && near(e, cp.at, P.jogWithinM))) t.jog = true;
      t.left -= DT;
      if (t.left <= 0) answer(k, null);
    });
  }
  /** 1-3: the talker's pick among the options shown */
  function say(e: Ent, o: number) {
    const k = S.talks.findIndex((t) => t?.who === e.id);
    if (k >= 0) answer(k, S.talks[k]!.opts[o - 1][0]);
  }

  function updateChalo() {
    if (!G || S.phase !== 'playing') return;
    S.chT += DT;
    const C = CR!.crowd;
    stepHold(); // the tolis that are already in: hausla frays, players standing with them steadies it
    stepHeld(); stepDharna(); stepBaat();
    // gates: the open time runs out, police walk back, and the gate shuts once the squad is in the gap again
    S.gates.forEach((g, k) => {
      if (g.state === 'open' && S.chT >= g.until) { g.state = 'back'; g.until = S.chT; g.sitIn = false; bitsCache = -1; }
      else if (g.state === 'back') {
        const home = S.ents.every((e) => e.cp !== k || Math.hypot(e.x - post(e).x, e.y - post(e).y) < 0.6 * map.tile);
        if (home || S.chT - g.until > 12 / (g.slow ? BT().recloseSpeed : 1)) { g.state = 'closed'; g.credit = 0; g.slow = false; bitsCache = -1; }
      }
      g.credit = walkable(k) ? Math.min(1, g.credit + C.passTolisPerS * DT) : 0;
    });
    // arrivals: tolis from the open entries, by weight, until arrivals stop
    if (S.chT < C.stopArrivalsAtS) {
      S.arrive += (C.arrivalPeoplePerS / C.toliSize) * DT;
      for (; S.arrive >= 1; S.arrive--) {
        const live = G.entries.filter((e) => e.closesAtS === null || S.chT < e.closesAtS);
        if (!live.length || tolis().length >= MAX_TOLIS) continue;
        let r = rnd() * live.reduce((a, e) => a + e.weight, 0), at = live[0];
        for (const e of live) { r -= e.weight; if (r < 0) { at = e; break; } }
        const t = mk('npc', 'N', '', 'toli');
        t.r = TOLI_R;
        t.toli = { n: C.toliSize, gate: -1, pass: -1, wait: 0, loss: 0, spot: 1 + rnd() * 3, nerve: 1, near: 0, edge: false, crush: false, drift: false };
        placeAt(t, at, map.tile * 0.8);
        S.arrived += C.toliSize;
      }
    }
  }

  /** Checkpoint distance in tiles on its field from tile i (-1 off the field). */
  const fieldD = (k: number, i: number) => G!.fields[k][i];
  /** The gate of ring `ring` to walk to from tile i: the nearest open one if it is not much further, else the nearest. */
  function chooseGate(ring: number, i: number): number {
    const reroute = CR!.crowd.rerouteIfOpenGateWithinM / map.mpt!;
    let best = -1, bd = Infinity, open = -1, od = Infinity;
    G!.cps.forEach((cp, k) => {
      if (cp.ring !== ring) return;
      const d = fieldD(k, i);
      if (d < 0) return;
      if (d < bd) { bd = d; best = k; }
      if (walkable(k) && d < od) { od = d; open = k; }
    });
    return open >= 0 && od <= bd + reroute ? open : best;
  }

  /**
   * A toli: walk to the chosen gate of the ring in front, queue while it is shut, pass when it is open (one toli per
   * second per gate), then pick a gate on the next ring; inside the inner ring, walk towards the stage and stand.
   */
  function thinkToli(e: Ent): Vec {
    const T = e.toli!, i = tileOf(e), lvl = G!.level[i], still = { x: 0, y: 0 };
    if (S.phase !== 'playing') return still;
    const follow = (F: Float32Array) => octileDir(map, F, e.x, e.y) || still;
    const nextField = () => (T.gate === -2 ? G!.stageField : G!.fields[T.gate]);
    if (T.pass >= 0) { // crossing the gap of gate T.pass
      const cp = G!.cps[T.pass];
      if (lvl > cp.ring && !G!.gapOf[i]) { T.pass = -1; T.wait = 0; }
      else return G!.fields[T.pass][i] > 0 ? follow(G!.fields[T.pass]) : follow(nextField());
    }
    if (lvl >= LEVEL_SITE && !G!.gapOf[i]) { // at the site: hold ground at some distance from the stage
      T.gate = -2;
      const d = G!.stageField[i];
      // hausla gone: edge back out of the crowd, away from the stage, until someone steadies them or they go home
      if (T.drift) { const out = octileDir(map, G!.stageField, e.x, e.y); if (out) return { x: -out.x, y: -out.y }; }
      return d < 0 || d <= T.spot ? still : follow(G!.stageField);
    }
    if (T.gate < 0 || G!.cps[T.gate].ring !== lvl) T.gate = chooseGate(lvl, i);
    else if (!walkable(T.gate) && S.tick % TICK_RATE === e.id % TICK_RATE) { // once a second: is an open gate close by?
      const k = chooseGate(lvl, i);
      if (walkable(k)) T.gate = k;
    }
    if (T.gate < 0) return still;
    const d = fieldD(T.gate, i);
    if (d < 0) { T.gate = -1; return still; }
    if (d <= 1.5) { // at the gap
      const g = S.gates[T.gate];
      if (!walkable(T.gate) || g.credit < 1) return still;
      g.credit -= 1;
      const cp = G!.cps[T.gate];
      T.pass = T.gate;
      T.gate = cp.ring + 1 >= LEVEL_SITE ? -2 : chooseGate(cp.ring + 1, cp.gaps[0]);
      return follow(G!.fields[T.pass][i] > 0 ? G!.fields[T.pass] : nextField());
    }
    return follow(G!.fields[T.gate]);
  }

  // ---------------------------------------------------------------- holding the site (plan: crowd-as-structure)
  /**
   * Once a second, for every toli holding the site: how many neighbours it has, whether it sits on the crowd's rim,
   * and whether it is crushed. Lopsidedness finds the rim, not a neighbour count: deep inside a block the directions
   * to the neighbours cancel out, while on the rim they all point the same way. A toli alone is always on the rim.
   */
  function surveyHold(site: Ent[]) {
    const H = CR!.hold!, near = H.nearM * METRE, crush = H.crushWithinM * METRE;
    for (const e of site) {
      const T = e.toli!;
      let n = 0, cn = 0, sx = 0, sy = 0;
      for (const o of site) {
        if (o === e) continue;
        const dx = o.x - e.x, dy = o.y - e.y, d = Math.hypot(dx, dy);
        if (d > near || d === 0) continue;
        n++; sx += dx / d; sy += dy / d;
        if (d <= crush) cn++;
      }
      T.near = n;
      T.edge = n === 0 || Math.hypot(sx, sy) / n > H.edgeLopsided;
      T.crush = cn > H.crushAbove;
    }
  }

  /**
   * Hausla, every tick: it frays on the rim, in sight of a cordon and in a crush, and steadies while a protester
   * stands with the toli -- which is the whole of the Steady verb, and why it needs no key: you steady people by
   * being there. Below driftBelow a toli edges out of the crowd (which puts it on the rim, which frays it faster:
   * the spiral is the point, and a player reaching it in time is what breaks it). Below leaveBelow its people start
   * going home, on the same schedule a tired queue already uses.
   */
  function stepHold() {
    const H = CR!.hold;
    if (!H || !o.hold) return;
    const site = tolis().filter(onSite);
    if (S.tick % TICK_RATE === 0) surveyHold(site);
    const D = H.drainPerS, steady = H.steadyM * METRE, cordon = H.cordonWithinM * METRE;
    const standing = humans().filter((h) => h.team === 'P');
    for (const e of site) {
      const T = e.toli!;
      let d = D.base + (T.edge ? D.edge : 0) + (T.crush ? D.crush : 0);
      if (nearCordon(e, cordon)) d += D.cordon;
      const helped = standing.some((h) => Math.hypot(h.x - e.x, h.y - e.y) <= steady);
      T.nerve = Math.max(0, Math.min(1, T.nerve + (helped ? H.steadyPerS : 0) * DT - d * DT));
      T.drift = T.nerve < H.driftBelow;
      if (T.nerve >= H.leaveBelow) continue;
      T.loss += DT;
      if (T.loss >= CR!.crowd.leaveOnePersonEveryS) { T.loss -= CR!.crowd.leaveOnePersonEveryS; T.n--; S.left++; }
    }
  }

  /** Is a cordon tile within `r` px? Checked on the ring bands only, so it costs a handful of tile reads. */
  function nearCordon(e: Ent, r: number): boolean {
    const step = map.tile, n = Math.ceil(r / step);
    for (let dy = -n; dy <= n; dy++) for (let dx = -n; dx <= n; dx++) {
      if (Math.hypot(dx, dy) * step > r) continue;
      const tx = Math.floor(e.x / step) + dx, ty = Math.floor(e.y / step) + dy;
      if (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h) continue;
      if (G!.cordon[ty * map.w + tx]) return true;
    }
    return false;
  }

  /** Waiting too long sends people home, one every leaveOnePersonEveryS after waitBeforeLeavingS. */
  function tireToli(e: Ent, moved: number) {
    const T = e.toli!, C = CR!.crowd;
    if (S.phase !== 'playing' || onSite(e) || moved > TOLI_SPEED * DT * 0.2) { if (onSite(e)) T.wait = 0; return; }
    T.wait += DT;
    if (T.wait <= C.waitBeforeLeavingS) return;
    T.loss += DT;
    if (T.loss >= C.leaveOnePersonEveryS) { T.loss -= C.leaveOnePersonEveryS; T.n--; S.left++; }
  }

  // ------------------------------------------------------------------ public api (Chalo)
  /** Debug (offline, tests): open the nearest shut gate to this player. */
  function debugOpen(id: number) {
    const e = byId.get(id);
    if (!o.debug || !G || !e || S.phase !== 'playing') return;
    let best = -1, bd = Infinity;
    G.cps.forEach((cp, k) => { const d = Math.hypot(cp.at.x - e.x, cp.at.y - e.y); if (!walkable(k) && d < bd) { bd = d; best = k; } });
    if (best >= 0) openGate(best);
  }
  /** A player's action (not movement, so not predicted): 'use' (E: sit in, talk, stand up) or 'say' with o = 1-3 (Baat). */
  function act(id: number, a: unknown, o?: unknown) {
    const e = byId.get(id);
    if (!G || !e || e.kind !== 'human' || e.team !== 'P' || S.phase !== 'playing') return;
    if (a === 'use') use(e);
    else if (a === 'say' && (o === 1 || o === 2 || o === 3)) say(e, o);
  }
  function chaloSnap(): ChaloSnap {
    const tk = S.talks.flatMap((t, k) => t ? [[k, t.who, t.ex, t.mood, Math.max(0, Math.ceil(t.left * 10)), LINE_KINDS.indexOf(t.hint[0]), t.hint[1],
      ...t.opts.flatMap(([kd, i]) => [LINE_KINDS.indexOf(kd), i]), t.last]] : []);
    const pressure = (k: number) => S.ents.filter((p) => p.role === 'police' && p.cp === k).length;
    const dh = S.sits.flatMap((s, k) => s ? [[k, s.sitters, Math.round(s.calm * 10), pressure(k) * 10, Math.floor(s.held * 10), Math.floor(s.low * 10), CR!.missions.dharna.durationS * 10]] : []);
    const snap: ChaloSnap = {
      g: gapBits(), f: S.fallen, r: frontRing(),
      cs: S.gates.map((g) => [GATE_CODE[g.state], g.state !== 'open' ? 0 : g.until === Infinity ? -1 : Math.ceil(g.until - S.chT), g.opened ? 1 : 0,
        Math.max(0, Math.ceil(g.cool - S.chT)), g.byTalk ? 1 : 0]),
      c: [siteCount(), S.goal, S.arrived, S.left, S.detained], w: S.won, t: Math.round(S.chT * 10) / 10,
    };
    if (tk.length) snap.tk = tk;
    if (dh.length) snap.dh = dh;
    return snap;
  }

  function thinkAmbient(e: Ent): Vec {
    if (e.idleT > 0) {
      e.idleT -= DT;
      if (e.idleT <= 0) e.target = pickTarget();
      return { x: 0, y: 0 };
    }
    const F = fieldOf(e.target);
    const d = fieldAt(map, F, e.x, e.y);
    if (d >= 0 && d <= (e.target === PLAZA ? plazaArrive : 1)) { e.idleT = rand(2, 7); return { x: 0, y: 0 }; }
    const dir = flowDir(map, F, e.x, e.y);
    if (!dir) { e.target = pickTarget(); return { x: 0, y: 0 }; }
    return dir;
  }

  /**
   * A friend keeps a few metres behind the player, each at their own place in the toli, on a path field to the player's
   * tile (rebuilt when the player moves to another tile). They hurry to catch up, and wait when they are close.
   */
  let followAt = -1, followF: Int16Array | null = null;
  function thinkFriend(e: Ent): { w: Vec; spd: number } {
    const lead = leader(), still = { w: { x: 0, y: 0 }, spd: SPEED };
    if (!lead) return still;
    const k = e.k ?? 0, ang = lead.face + Math.PI + ((k % 2) * 2 - 1) * (0.5 + 0.25 * Math.floor(k / 2));
    const back = (2.2 + 1.2 * Math.floor(k / 2)) * METRE;
    const tx = lead.x + Math.cos(ang) * back, ty = lead.y + Math.sin(ang) * back;
    const d = Math.hypot(tx - e.x, ty - e.y), far = Math.hypot(lead.x - e.x, lead.y - e.y);
    if (d < 0.8 * METRE) return still;
    const spd = Math.min(SPEED * (far > 10 * METRE ? 1.4 : 1.05), (d / DT) * 0.5);
    if (far < 3 * map.tile) return { w: { x: (tx - e.x) / d, y: (ty - e.y) / d }, spd }; // in sight: straight there
    const lt = tileOf(lead);
    if (lt !== followAt) { followAt = lt; followF = buildField(map, [lead]); }
    return { w: flowDir(map, followF!, e.x, e.y) || { x: (tx - e.x) / d, y: (ty - e.y) / d }, spd };
  }

  /** Kafila: mill round the gate while gathering; march the corridor to the zone; then spread through it and stand. */
  function thinkKafila(e: Ent): { w: Vec; spd: number } {
    const still = { w: { x: 0, y: 0 }, spd: SPEED * NPC_SPEED };
    if (S.phase !== 'playing' || S.stage < 0) return still;
    const P = plan();
    if (S.stage >= 1 && !e.arrived) {
      if (P.masks.pad[tileOf(e)]) { e.arrived = true; e.target = Math.floor(rnd() * P.standPts.length); }
      else {
        const d = octileDir(map, P.march, e.x, e.y) || { x: 0, y: 0 };
        // lean towards their lane; the corridor's edge (and the crowd) keep them on the road
        const wx = d.x - d.y * e.lane * 0.45, wy = d.y + d.x * e.lane * 0.45, l = Math.hypot(wx, wy) || 1;
        return { w: { x: wx / l, y: wy / l }, spd: SPEED * ST!.march.speedFactor };
      }
    }
    if (e.idleT > 0) { e.idleT -= DT; return still; }
    const stand = S.stage >= 1, pts = stand ? P.standPts : P.gatherPts, k = stand ? P.masks.stand : P.masks.gather;
    const i = e.target % pts.length;
    const F = fieldFor(map, `${P.key}:${stand ? 's' : 'g'}:${i}`, () => [pts[i]], k);
    const d = fieldAt(map, F, e.x, e.y);
    if (d >= 0 && d <= 1) {
      e.idleT = stand ? rand(ST!.stand.idleS[0], ST!.stand.idleS[1]) : rand(2, 6);
      e.target = Math.floor(rnd() * pts.length);
      return still;
    }
    const dir = flowDir(map, F, e.x, e.y);
    if (!dir) { e.target = Math.floor(rnd() * pts.length); return still; }
    return { w: dir, spd: SPEED * NPC_SPEED };
  }

  function stepBodies() {
    for (const e of S.ents) {
      // an arrival walks in from outside the stage area; once inside it, it is bounded like everyone else
      if (e.free && ST && S.stage >= 0 && S.phase === 'playing') { e.free = false; const m = maskOf(e); e.free = !m || !m[tileOf(e)]; }
      const ox = e.x, oy = e.y, k = maskOf(e);
      let moved: number, spd: number, wx = 0, wy = 0;
      if (e.kind === 'human') {
        // each input moves the body exactly once, as the client predicted it. A tick with no input waits and banks the
        // time; a late burst spends it. Never more steps than ticks, so flooding inputs cannot add speed.
        e.bank = Math.min(INPUT_BANK, e.bank + 1); moved = 0;
        for (let inp = e.inq.shift(); inp; inp = e.bank >= 1 ? e.inq.shift() : undefined) {
          e.bank--; e.ack = inp.s; e.last = { x: inp.x, y: inp.y, r: inp.r || 0 };
          if (e.held) { e.last = { x: 0, y: 0, r: 0 }; continue; } // in the bus: inputs are acked, nobody walks (the client knows, from me.dt)
          if ((e.sit ?? -1) >= 0 && (inp.x || inp.y)) e.sit = -1; // walking stands you up
          moved += moveHuman(map, e, inp.x, inp.y, e.last.r, { ...RULES, mask: k }, DT);
        }
        wx = e.last.x; wy = e.last.y;
        spd = SPEED;
      } else if (e.role === 'police') {
        const p = post(e), dx = p.x - e.x, dy = p.y - e.y, d = Math.hypot(dx, dy);
        const g = S.gates[e.cp!];
        spd = SPEED * NPC_SPEED * (g?.slow && g.state === 'back' ? BT().recloseSpeed : 1); // after a talk, they take their time
        if (d > 0.15 * map.tile) { wx = dx / d; wy = dy / d; }
        moved = moveBody(map, e, wx, wy, Math.min(spd, d / DT), DT);
        if (!wx && !wy) e.face = Math.atan2(G!.centre.y - e.y, G!.centre.x - e.x) + Math.PI; // face out, towards the crowd
      } else if (e.role === 'toli') {
        const w = thinkToli(e); wx = w.x; wy = w.y;
        spd = e.toli!.drift && CR!.hold ? TOLI_SPEED * CR!.hold.driftSpeedFactor : TOLI_SPEED; // drifting out is a trudge
        moved = moveBody(map, e, wx, wy, spd, DT, k);
        tireToli(e, moved);
      } else {
        if (e.role === 'kafila') { const t = thinkKafila(e); wx = t.w.x; wy = t.w.y; spd = t.spd; }
        else if (e.role === 'friend') { const t = thinkFriend(e); wx = t.w.x; wy = t.w.y; spd = t.spd; }
        else { const w = thinkAmbient(e); wx = w.x; wy = w.y; spd = SPEED * NPC_SPEED; }
        moved = moveBody(map, e, wx, wy, spd, DT, k);
      }
      if (moved > spd * DT * 0.3) e.face = Math.atan2(e.y - oy, e.x - ox);
      if (e.kind === 'npc' && (e.role === 'ambient' || e.role === 'kafila')) {
        if ((wx || wy) && moved < spd * DT * 0.15) {
          e.stuckT += DT;
          if (e.stuckT > 1.5) { e.stuckT = 0; e.target = e.role === 'kafila' ? Math.floor(rnd() * 12) : pickTarget(); }
        } else e.stuckT = 0;
      }
    }
  }

  // push bodies apart that stand closer than their radii plus the personal-space gap. Humans are never displaced by NPCs, so client prediction (which doesn't know the
  // NPCs) stays accurate. Pairs come from a spatial hash once there are enough bodies for O(n^2) to cost.
  function pushApart(a: Ent, b: Ent) {
    const dx = b.x - a.x, dy = b.y - a.y, rr = a.r + b.r + GAP;
    if (dx > rr || dx < -rr || dy > rr || dy < -rr) return;
    const dd = dx * dx + dy * dy;
    if (dd >= rr * rr) return;
    const d = Math.sqrt(dd) || 0.01, ov = rr - d;
    const nx = dx / d, ny = dy / d;
    // police hold their ground against the crowd and never touch a player (prediction does not know them)
    if ((a.role === 'police' && b.kind === 'human') || (b.role === 'police' && a.kind === 'human')) return;
    const wa = a.role === 'police' || (a.kind === 'human' && b.kind === 'npc') ? 0 : b.role === 'police' || (a.kind === 'npc' && b.kind === 'human') ? 1 : 0.5;
    a.x -= nx * ov * wa; a.y -= ny * ov * wa;
    b.x += nx * ov * (1 - wa); b.y += ny * ov * (1 - wa);
    if (wa > 0) collide(map, a, a.r, maskOf(a));
    if (wa < 1) collide(map, b, b.r, maskOf(b));
  }
  const grid = new Map<number, Ent[]>();
  function separate() {
    const E = S.ents;
    if (!ST || E.length <= ST.separation.spatialHashAbove) {
      for (let i = 0; i < E.length; i++) for (let j = i + 1; j < E.length; j++) pushApart(E[i], E[j]);
      return;
    }
    const cell = Math.max(ST.separation.cellPx, 2 * Math.max(PLAYER_R, NPC_R, G ? TOLI_R : 0) + GAP); // a pair can only touch across adjacent cells
    for (const l of grid.values()) l.length = 0;
    for (const e of E) {
      const key = Math.floor(e.y / cell) * 65536 + Math.floor(e.x / cell);
      let l = grid.get(key); if (!l) grid.set(key, (l = []));
      l.push(e);
    }
    for (const [key, l] of grid) {
      if (!l.length) continue;
      for (let i = 0; i < l.length; i++) for (let j = i + 1; j < l.length; j++) pushApart(l[i], l[j]);
      for (const off of [1, 65535, 65536, 65537]) { // east, and the three cells below: each neighbour pair once
        const m = grid.get(key + off);
        if (m) for (const a of l) for (const b of m) pushApart(a, b);
      }
    }
  }

  function step() {
    S.tick++;
    updatePhase();
    updateStage();
    updateChalo();
    stepBodies();
    separate(); separate(); separate(); // three passes: one leaves pairs that a pushed neighbour (or a wall) shoved back inside the personal space
    if (G && S.ents.some((e) => e.toli && e.toli.n <= 0)) {
      for (const e of S.ents) if (e.toli && e.toli.n <= 0) byId.delete(e.id);
      S.ents = S.ents.filter((e) => !e.toli || e.toli.n > 0);
    }
    rowsCache = null;
  }

  // ------------------------------------------------------------------ snapshots
  let rowsCache: number[][] | null = null;
  /** 0 protester, 1 police (players and Chalo's squads), 2 npc, 3 toli (its row also carries its people count) */
  const type = (e: Ent) => (e.role === 'toli' ? 3 : e.role === 'friend' ? 4 : e.role === 'police' ? 1 : e.kind === 'npc' ? 2 : e.team === 'C' ? 1 : 0);
  function rows(): number[][] {
    // positions to 0.1 px: on a metre-scale map a whole px is 0.19 m, and walkers move about 1 px a tick
    if (!rowsCache) rowsCache = S.ents.map((e) => {
      const r = [e.id, type(e), Math.round(e.x * 10) / 10, Math.round(e.y * 10) / 10, Math.round(e.face * 100)];
      if (e.toli) { r.push(e.toli.n); r.push(Math.round(e.toli.nerve * 100)); } // its people, and its hausla 0..100
      else if (e.role === 'friend') r.push(e.k ?? 0);
      else if (e.kind === 'npc') r.push(e.role === 'kafila' ? 1 : 0); // the stage's crowd or a passer-by: who may carry a poster
      else if (e.held) r.push(2); // a player: 2 in the bus, 1 sitting in, nothing standing
      else if ((e.sit ?? -1) >= 0) r.push(1);
      return r;
    });
    return rowsCache;
  }
  function counts() {
    let P = 0, C = 0;
    for (const e of S.ents) if (e.kind === 'human') { if (e.team === 'C') C++; else P++; }
    return { P, C };
  }
  function kafilaCounts(): number[] {
    const K = kafila();
    if (!K.length || S.start < 0) return [0, 0, 0, 0];
    const { gather, march, pad } = plan().masks;
    let g = 0, m = 0, p = 0;
    for (const e of K) { const i = tileOf(e); g += gather[i]; m += march[i]; p += pad[i]; }
    return [K.length, g, m, p];
  }
  function snapshot(id: number | null): Snapshot {
    const e = id === null ? undefined : byId.get(id);
    const pt = S.phase === 'playing' ? S.roundT : S.phase === 'lobby' ? 0 : S.phaseT;
    // area of interest: every human (the minimap shows them), but npcs only near the player
    let rs = rows();
    if (e && AOI < Infinity) {
      const r2 = AOI * AOI;
      rs = rs.filter((r) => r[1] !== 2 || (r[2] - e.x) ** 2 + (r[3] - e.y) ** 2 <= r2);
    }
    const snap: Snapshot = {
      t: 's', tk: S.tick, ph: S.phase, pt: Math.ceil(Math.max(0, pt)), round: S.round, n: counts(),
      me: e && e.team !== 'N' ? { id: e.id, team: e.team, x: e.x, y: e.y, vx: e.vx, vy: e.vy, ack: e.ack, st: e.st, jg: e.jog ? 1 : 0, rs: e.rest } : null,
      e: rs,
    };
    if (snap.me && e?.held) snap.me.dt = Math.ceil(e.held); // seconds left in the bus
    if (snap.me && (e?.sit ?? -1) >= 0) snap.me.si = e!.sit! + 1; // the gate sat in at, + 1
    if (G) snap.ch = chaloSnap();
    else if (ST) { snap.sg = S.stage; snap.sv = S.start; snap.sgt = Math.floor(S.stageT); snap.k = kafilaCounts(); if (e?.free) snap.fr = 1; }
    if (S.tick % TICK_RATE === 0 || (e && e.kind === 'human' && e.sentNames === false)) {
      snap.nm = S.ents.filter((h) => h.kind === 'human').map((h) => [h.id, h.name, h.av]);
      if (e) e.sentNames = true;
    }
    return snap;
  }

  return {
    S, map, opts: o, addPlayer, place, removePlayer, setTeam, rename, holdLobby, input, step, snapshot, humans, kafila, friends, addFriends, maskOf, spend, plan: ST ? plan : null,
    geo: G, tolis, siteCount, gapBits, openGate, debugOpen, act, detain,
  };
}

export type Sim = ReturnType<typeof createSim>;
